/**
 * Utilitários de uso geral do front.
 *
 * O quê: reúne funções pequenas e sem dependência de domínio, usadas por
 * qualquer módulo. Hoje contém apenas a composição de classes CSS.
 *
 * Como: `cn` resolve o problema clássico de sobrescrever classe do Tailwind —
 * `"px-2"` e `"px-4"` juntas geram um resultado indefinido, porque quem vence é
 * a ordem no arquivo CSS, não a ordem no atributo. O tailwind-merge entende os
 * grupos de utilitários e mantém só a última de cada grupo.
 *
 * Utiliza: clsx para aceitar condicionais e listas, tailwind-merge para
 * desempatar utilitários conflitantes. É o `cn()` esperado pelos componentes
 * do shadcn/ui.
 */
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Combina classes CSS resolvendo conflitos entre utilitários do Tailwind.
 *
 * Aceita strings, condicionais e listas aninhadas — qualquer forma que o clsx
 * entenda. Valores falsos são descartados, então `cn('base', ativo && 'ring-2')`
 * funciona sem `undefined` sobrando na saída. Quando duas classes disputam a
 * mesma propriedade, a última informada prevalece, o que permite ao chamador
 * sobrescrever o estilo padrão de um componente via prop `className`.
 *
 * @param entradas - Classes a combinar, em qualquer formato aceito pelo clsx.
 * @returns String de classes pronta para o atributo `class`, sem conflitos.
 */
export function cn(...entradas: ClassValue[]): string {
  return twMerge(clsx(entradas));
}
